import React from 'react';
import JsonView from '@uiw/react-json-view';
import { darkTheme } from '@uiw/react-json-view/dark';
import { lightTheme } from '@uiw/react-json-view/light';

// Utility to detect TypedArrays
const isTypedArray = (obj) => obj && typeof obj === 'object' && obj.buffer instanceof ArrayBuffer && typeof obj.BYTES_PER_ELEMENT === 'number' && typeof obj.length === 'number' && typeof obj.slice === 'function';

// Utility to truncate typed arrays for performance, with circular reference protection
const truncateTypedArrays = (obj, seen = new WeakSet()) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // Handle Node.js Buffer serialization: { type: 'Buffer', data: [...] }
  if (obj.type === 'Buffer' && Array.isArray(obj.data)) {
    const limited = Array.from(obj.data.slice(0, 20));
    return {
      type: 'Buffer',
      data: obj.data.length > 20 ? [...limited, '...(Limited for performance)'] : obj.data
    };
  }

  if (isTypedArray(obj)) {
    const limited = Array.from(obj.slice(0, 20));
    return obj.length > 20 ? [...limited, '...(Limited for performance)'] : obj;
  }

  if (seen.has(obj)) {
    return '[Circular]';
  }
  seen.add(obj);

  if (Array.isArray(obj)) {
    return obj.map((item) => truncateTypedArrays(item, seen));
  }

  if (Object.prototype.toString.call(obj) === '[object Object]') {
    const result = {};
    for (const key in obj) {
      result[key] = truncateTypedArrays(obj[key], seen);
    }
    return result;
  }

  return obj;
};

const JsonViewer = ({ json, loading, collapsed = 3, theme = 'light', objectSortKeys = true }) => {
  const computedTheme = theme === 'dark' ? darkTheme : lightTheme;
  const safeJson = truncateTypedArrays(json);

  const isNumericDepth = typeof collapsed === 'number' && Number.isFinite(collapsed);
  const collapsedProp = typeof collapsed === 'boolean' ? collapsed : undefined;
  const expandByDepth = isNumericDepth ? (isExpanded, { level }) => level < collapsed : undefined;

  return (
    <div className={'comp-json-viewer'}>
      {typeof json === 'object' && json !== null && !(json instanceof Date) && (
        <>
          {loading && <div className={'loading'}>Loading...</div>}
          {!loading && (
            <JsonView value={safeJson} color={computedTheme} collapsed={collapsedProp} shouldExpandNodeInitially={expandByDepth} enableClipboard={false} displayObjectSize={true} displayDataTypes={true} highlightUpdates={false} objectSortKeys={objectSortKeys}>
              <JsonView.CountInfo
                render={({ 'data-length': length, ...props }, { value }) => {
                  const isArray = Array.isArray(value);
                  return isArray ? <span {...props}>({length})</span> : <span />;
                }}
              />
              <JsonView.Ellipsis
                render={({ 'data-expanded': isExpanded, className, ...props }, { value }) => {
                  const showPreview = isExpanded && (Array.isArray(value) || (typeof value === 'object' && Object?.keys(value)?.length > 0));
                  const previewValue = JSON.stringify(value)?.slice(1, -1);
                  if (showPreview) {
                    return <span className={`${className} w-rjv-preview`}>{previewValue}</span>;
                  }
                  if (!value || Object?.keys(value)?.length === 0) {
                    return <span />;
                  }
                  if (isExpanded) {
                    return <span {...props} />;
                  }
                  return <span />;
                }}
              />
              <JsonView.Quote render={() => <></>} />
              <JsonView.String render={(_, { type }) => type === 'type' && <span />} />
              <JsonView.Int render={(_, { type }) => type === 'type' && <span />} />
              <JsonView.Float render={(_, { type }) => type === 'type' && <span />} />
              <JsonView.True render={(_, { type }) => type === 'type' && <span />} />
              <JsonView.False render={(_, { type }) => type === 'type' && <span />} />
              <JsonView.Date render={(_, { type }) => type === 'type' && <span />} />
            </JsonView>
          )}
        </>
      )}
      {(['string', 'number', 'boolean'].includes(typeof json) || json === null || json instanceof Date) && (
        <>
          {loading && <div className={'loading'}>Loading...</div>}
          {!loading && <pre className="json-pre">{String(json)}</pre>}
        </>
      )}
    </div>
  );
};

export default JsonViewer;
